import type { ReactNode } from 'react';

import type { MeasurementRow, MeasurementSection } from './measurement-sections';

import { ReportPage } from '@/components/report';
import { cn } from '@/lib/utils';
import { formatCarmAngle } from '@/workflows/vascular/common/utils/carm-angles-formatter';

export interface EvarReportMeasurementsPageProps {
    /** Sections to render on this page (pre-paginated by the preview container). */
    sections: readonly MeasurementSection[];
    pageNumber: number;
    totalPages: number;
    /** True on every measurements page after the first. */
    continued?: boolean;
}

/**
 * One measurements report page: measurement + C-arm view sections grouped
 * by category. Purely presentational — the preview container builds and
 * paginates the sections (see measurement-sections.ts) so each page fits
 * the fixed-height A4 sheet.
 */
export function EvarReportMeasurementsPage({
    sections,
    pageNumber,
    totalPages,
    continued,
}: EvarReportMeasurementsPageProps) {
    return (
        <ReportPage pageNumber={pageNumber} totalPages={totalPages}>
            <h2 className="mb-3 border-b border-gray-300 pb-1 text-sm font-semibold uppercase tracking-wide text-gray-700">
                Detailed Measurements{continued ? ' (continued)' : ''}
            </h2>
            {sections.length > 0 ? (
                <div className="flex flex-col">
                    {sections.map((section) => (
                        <TableSection key={section.key} title={section.title}>
                            {section.rows.map((row) => (
                                <Row key={row.id} row={row} />
                            ))}
                        </TableSection>
                    ))}
                </div>
            ) : (
                <p className="text-xs text-gray-400">No measurement data available.</p>
            )}
        </ReportPage>
    );
}

// ============================================================================
// Sub-components
// ============================================================================

function TableSection({ title, children }: { title: string; children: ReactNode }) {
    return (
        <div className="mb-3">
            <div className="mb-1 border-b border-gray-300 pb-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                {title}
            </div>
            {children}
        </div>
    );
}

function formatValue(value: number | null | undefined, unit: string): string {
    if (value == null || isNaN(value)) return 'N/A';
    return `${value.toFixed(1)} ${unit}`;
}

function Row({ row }: { row: MeasurementRow }) {
    const indented = row.kind === 'value' && row.indented;
    return (
        <div
            className={cn(
                'flex items-center justify-between border-b border-gray-100 px-1 py-1 text-[11px] hover:bg-gray-50',
                indented && 'pl-4'
            )}
        >
            <span className={cn('font-mono text-gray-700', indented && 'text-gray-500')}>{row.name}</span>
            <span className="tabular-nums font-mono text-gray-800">
                {row.kind === 'value'
                    ? formatValue(row.value, row.unit)
                    : `${formatCarmAngle(row.craniocaudal, 'craniocaudal')} / ${formatCarmAngle(row.oblique, 'oblique')}`}
            </span>
        </div>
    );
}
